import type { DbExecutor } from "./executor";
import type { GcCompensationService } from "./gcCompensationService";
import {evaluateGcCompensationSources,evaluateConditionalGcCompensationSources} from '../engine/gcCompensation';
import {prepareAutomaticFundingSources,automaticUnscheduledProject} from '../engine/automaticFundingPreparation';
import {illustrateAutomaticPermissions,illustrateAutomaticClosingReleases} from '../engine/automaticFundingPermissions';
import {evaluateAutomaticFunding} from '../engine/automaticFundingEvaluation';
import {generateId} from './ids';
import {activeGcPortions} from '../engine/gcSourceValidation';
import type { CategoryBudgetService } from "./categoryBudgetService";
import type { ProjectRepository } from "./repositories/projectRepository";
import type { ProposedCostRepository } from "./repositories/proposedCostRepository";
import type { TaskRepository } from "./repositories/taskRepository";
import type { FinancingRepository } from "./repositories/financingRepository";
import type { CapitalRepository } from "./repositories/capitalRepository";
import type { ReconciliationService } from "./reconciliationService";
import type { ProjectFinancialsService } from "./projectFinancialsService";
import type { AcquisitionRepository } from "./repositories/acquisitionRepository";
import type { BidService } from "./bidService";
import type { DesignRepository } from "./repositories/designRepository";
import { applySelectedQuotes } from "../engine/bidPackage";
import { acquisitionExpensesCents } from "../engine/financials";
import { addDays, addMonth } from "../engine/financing";
import {
  applyConservative,
  buildProjectPlan,
  cashPosition,
  combinePortfolio,
  projectedFutureFinancingCents,
  shiftPlan,
  stabilizedRentalEconomics,
  summarizeFacilities,
  type CapitalInputs,
  type CashPosition,
  type DrawInput,
  type FacilityInput,
  type FacilitySummary,
  type PortfolioResult,
  type ProjectPlan,
  type ProjectPlanInputs,
  type RentalEconomics,
} from "../engine/capitalPlan";
import type { PlanSettings, ScenarioProject, Strategy } from "../domain/financing";
import {
  buildRecommendations,
  optimizeStarts,
  type OptimizerResult,
  type PlanObjective,
  type Recommendation,
} from "../engine/portfolioOptimizer";
import type { Cents } from "../domain/money";
import { catalogFundingGcApprovalSources } from "../engine/fundingGcCatalog";
import type { CompanyFundingScenario } from "../domain/companyFunding";
import { validateCompanyFundingScenario } from "../engine/companyFundingValidation";
import { companyFundingNeeds } from "../engine/companyFundingNeeds";
import { companyFundingProjection } from "../engine/companyFundingProjection";
import { optimizeCompanyFunding } from "../engine/companyFundingOptimizer";
import { prepareProjectFundingForecast } from "../engine/fundingProjectForecast";
import { resolveFundingCashAnchors, type StatementCashEvidence } from "../engine/fundingCashAnchors";
import type { BankReconciliation97Service } from "./bankReconciliation97Service";
import type { Accounting97Service } from "./accounting97Service";
import type { DrawRequestService } from "./drawRequestService";
import { applyFundingProjectReview, scenarioFundingDefaults } from "../engine/fundingProjectPolicy";
import { FUNDING_ENGINE_VERSION, fundingFingerprint } from "../engine/fundingProvenance";
import { applyFundingRateAssumptions } from "../engine/fundingRateAssumptions";
import type { FundingLoanRateOverride } from "../domain/fundingProject";
import type { BudgetApprovalService } from "./budgetApprovalService";
import { companyFundingOutlook } from "../engine/companyFundingOutlook";
import { classifyActualCash } from "../engine/cashClassification";
import type { FinancialBucket } from "../domain/buckets";
import { financialCalendar, type FinancialCalendarModel } from "../engine/financialCalendar";
import type { CommitmentService } from "./commitmentService";
import { cashActivityEffect,companyPaymentEffect } from "../engine/commitments";
import type { FinancialEvent } from "../engine/capitalPlan";

/**
 * CAPITAL PLAN ASSEMBLY (spec §73).
 *
 * This service does NO financial arithmetic. It gathers the truths the
 * application already holds — the active design's working estimate with
 * selected quotes substituted, the normalized actual-cost events, the CPM
 * schedule, financing terms and draws, rental assumptions, the sale
 * record, capital sources and scenario fixtures — and hands them to the
 * pure engines in src/engine/financing.ts and src/engine/capitalPlan.ts.
 *
 * A PLANNING start override shifts a COPY of the derived events; the
 * project's committed schedule is never touched (spec §73R).
 */

export type PlanVariant = "expected" | "conservative";

export interface PortfolioPlan {
  variant: PlanVariant;
  horizonEnd: string;
  plans: ProjectPlan[];
  result: PortfolioResult;
  settings: PlanSettings;
  /** The capital the plan was given (§88B): what the screen explains a shortfall against. */
  capital: CapitalInputs;
  /** Active capital sources entered; 0 means the plan started from nothing, not that nothing exists. */
  capitalSourceCount: number;
  businessEvents?: FinancialEvent[];
  businessFacilities?: FacilitySummary[];
}

const today = (): string => new Date().toISOString().slice(0, 10);

/**
 * What the execution forecast tells the rest of the system about ONE
 * project's planning (spec §75D/R): when the lender sends money, when the
 * schedule finishes each feature's work, and when the exit is expected to
 * close. Injected, so the dependency stays one-way.
 */
export interface ForecastBridge {
  draws: DrawInput[];
  featureWorkFinish: Map<string, string>;
  expectedExitDate: string | null;
}

export class CapitalPlanService {
  private gcCompensation:GcCompensationService|null=null;
  setGcCompensation(source:GcCompensationService):void{this.gcCompensation=source;}
  private fundingEvidence: {bank:BankReconciliation97Service;accounting:Accounting97Service;draws:DrawRequestService;approval?:BudgetApprovalService}|null=null;
  setFundingEvidence(bank:BankReconciliation97Service,accounting:Accounting97Service,draws:DrawRequestService,approval?:BudgetApprovalService):void{this.fundingEvidence={bank,accounting,draws,approval};}
  /** Read-only automatic draft: no scenario must be created or saved to calculate. */
  async prepareAutomaticFunding(monthlyCents:number|null=null){
    if(monthlyCents!==null&&(!Number.isSafeInteger(monthlyCents)||monthlyCents<0||monthlyCents>1_000_000_000_000))throw new Error('Enter a bounded monthly cash amount in exact cents.');
    const asOfDate=today();
    const [saved,settings,scenarios,statements,rows]=await Promise.all([this.capital.listFundingScenarios(),this.capital.getPlanSettings(),this.capital.listScenarios(),this.fundingStatementOptions(),this.db.select<{id:string;name:string}>('SELECT id,name FROM projects ORDER BY id LIMIT 201')]);
    if(rows.length>200)throw new Error('Automatic planning supports at most 200 projects; none were silently omitted.');
    const facilities=await this.financing.listFacilities(null);
    const projects:Array<{id:string;name:string;finish:string|null;exit:string|null}>=[];
    for(const row of rows){
      const [schedule,sale,loans]=await Promise.all([this.tasks.scheduleWithTasks(row.id),this.projectFinancials.getSale(row.id),this.financing.listFacilities(row.id)]);
      projects.push({...row,finish:schedule.schedule.projectFinish,exit:sale?.saleDate??null});facilities.push(...loans);
    }
    return prepareAutomaticFundingSources({id:generateId(),asOfDate,monthlyCents,saved:saved[0]?.scenario,projects,facilities,settings,scenarios,statements});
  }
  /** Hypothetical evaluation never creates or updates economic or planning records. */
  async calculateAutomaticFunding(monthlyCents:number,overrides?:CompanyFundingScenario,variant:'expected'|'conservative'='expected',explicit?:{taxReserveExplicit:boolean;companyReserveExplicit:boolean}){
    await this.db.whenIdle?.();const before=await this.fundingReadStamp();
    const prepared=await this.prepareAutomaticFunding(monthlyCents);
    const scenario=overrides?{...structuredClone(overrides),asOfDate:prepared.scenario.asOfDate,target:{...overrides.target,monthlyCents,startDate:overrides.target.startDate<prepared.scenario.asOfDate?prepared.scenario.asOfDate:overrides.target.startDate}}:prepared.scenario;
    if(overrides)scenario.facilityPermissions=[...prepared.scenario.facilityPermissions.map(row=>overrides.facilityPermissions.find(prior=>prior.facilityId===row.facilityId)??row),...overrides.facilityPermissions.filter(row=>!prepared.scenario.facilityPermissions.some(current=>current.facilityId===row.facilityId))];
    if(overrides&&explicit?.taxReserveExplicit===false)scenario.target.personalTaxReserveMonthlyCents=prepared.scenario.target.personalTaxReserveMonthlyCents;
    if(overrides&&explicit?.companyReserveExplicit===false)scenario.reservePolicy.minimumUnrestrictedCents=prepared.scenario.reservePolicy.minimumUnrestrictedCents;
    const assumptions=prepared.assumptions.filter(item=>!(overrides&&(explicit?.taxReserveExplicit??true)&&item.startsWith('Personal tax provision'))&&!(overrides&&(explicit?.companyReserveExplicit??true)&&(item.startsWith('Suggested minimum unrestricted')||item.startsWith('Using the existing Capital Plan minimum'))));
    const result=await this.fundingOptimizationRead(scenario,variant,{assumptions});
    if(before!==await this.fundingReadStamp())throw new Error('Financial sources changed while calculating. Calculate again to use one consistent snapshot.');
    return {...result,automatic:{assumptions:result.optimization.assumptions??prepared.assumptions,conditional:result.optimization.evidenceStatus!=='verified-sources',scenario}};
  }
  async fundingSourceOptions(){
    const projects=await this.db.select<{id:string;name:string}>("SELECT id,name FROM projects ORDER BY name,id LIMIT 201");
    if(projects.length>200)throw new Error("The company planner supports at most 200 included projects; no projects were silently omitted.");
    const facilities=await this.financing.listFacilities(null);
    for(const project of projects)facilities.push(...await this.financing.listFacilities(project.id));
    const approvedIds=await this.db.select<{id:string;project_id:string}>("SELECT id,project_id FROM draw_requests WHERE status IN ('approved','partially-approved','partially-funded','funded','retained') ORDER BY project_id,request_date,id LIMIT 501");
    const details:import("../domain/drawRequest").DrawRequestDetail[]=[];
    if(this.fundingEvidence&&approvedIds.length<=500)for(const row of approvedIds)details.push(await this.fundingEvidence.draws.get(row.project_id,row.id));
    const gcCatalog=catalogFundingGcApprovalSources(details,facilities);
    const gcSourceIssues=approvedIds.length>500?["Approved fee source catalog exceeds 500 requests; no source was silently omitted."]
      :!this.fundingEvidence&&approvedIds.length?["Retained approval evidence is unavailable; no GC source was inferred."]:gcCatalog.issues;
    const gcEvidence=await this.gcCompensation?.evidence(projects.map(row=>row.id))??[];
    const gcBindings=gcEvidence.flatMap(snapshot=>activeGcPortions(snapshot.portions).flatMap(portion=>{
      if(portion.kind!=='draw'||!portion.fundingClaims.some(claim=>claim.amountCents>0))return[];
      const request=snapshot.drawRequests.find(row=>row.request.id===portion.drawRequestId);
      return request?.request.facilityId?[{id:portion.id,projectId:snapshot.projectId,facilityId:request.request.facilityId,label:`${request.request.requestDate} · ${request.request.lines.find(line=>line.id===portion.lineId)?.description??'GC funded portion'}`}]:[];
    }));
    return {projects,facilities:facilities.map(f=>({id:f.id,name:f.name,projectId:f.projectId})),scenarios:(await this.capital.listScenarios()).map(scenarioFundingDefaults),gcSources:gcCatalog.sources,gcSourceIssues,gcBindings};
  }
  async fundingStatementOptions(){
    if(!this.fundingEvidence)return [];
    const [statements,accounts]=await Promise.all([this.fundingEvidence.bank.listStatements(),this.fundingEvidence.accounting.accounts()]);
    return statements.filter(statement=>statement.closedAt&&statement.convention==="debit-positive"&&accounts.some(account=>account.id===statement.accountId&&account.type==="asset"&&["bank","cash"].includes(account.bankKind??"")&&!account.archivedAt)).map(statement=>({statementId:statement.id,accountId:statement.accountId,accountName:accounts.find(account=>account.id===statement.accountId)!.name,periodEnd:statement.periodEnd,closingCents:statement.closingCents,reference:statement.reference}));
  }
  private commitmentSource: CommitmentService | null = null;
  setCommitments(source: CommitmentService): void { this.commitmentSource=source; }
  /** Read-only planning. No expense, draw request, loan activity or journal is created. */
  async fundingOptimization(scenario: CompanyFundingScenario, variant: "expected" | "conservative" = "expected") {
    await this.db.whenIdle?.();
    const before=await this.fundingReadStamp();
    const result=await this.fundingOptimizationRead(scenario,variant);
    const after=await this.fundingReadStamp();
    if(before!==after)throw new Error("Financial sources changed while the plan was being evaluated. Evaluate again to use one consistent source snapshot.");
    return result;
  }
  private async fundingReadStamp(){
    const own=await this.db.select<{version:number}>("SELECT total_changes() AS version");
    const other=await this.db.select<{data_version:number}>("PRAGMA data_version");
    return `${own[0]?.version}:${other[0]?.data_version}`;
  }
  private async fundingOptimizationRead(scenario: CompanyFundingScenario, variant: "expected" | "conservative",automatic?:{assumptions:string[]}) {
    if (!validateCompanyFundingScenario(scenario).valid) throw new Error("Review invalid planning assumptions before calculating.");
    const periods=companyFundingNeeds(scenario,variant).periods;
    const dates=[periods[periods.length-1]?.through ?? scenario.asOfDate,...(scenario.target.customRequirements??[]).map(item=>item.date),...scenario.acquisitions.map(item=>item.anticipatedDate),
      ...scenario.facilityPermissions.map(item=>item.confirmedPayoffDate??scenario.asOfDate)].sort();
    let endDate=dates[dates.length-1];
    if (endDate>addDays(scenario.asOfDate,3660)) throw new Error("Choose a planning horizon of at most ten years.");
    const projectRows=await this.db.select<{id:string}>("SELECT id FROM projects ORDER BY id LIMIT 201");
    if(projectRows.length>200)throw new Error("The company planner cannot certify more than 200 projects; none were silently omitted.");
    const gcSourceSnapshots=await this.gcCompensation?.evidence(projectRows.map(row=>row.id),scenario.asOfDate)??[];
    const automaticConditions=(automatic?.assumptions??[]).filter(item=>!item.startsWith('Personal cash period inferred from project completion/closing dates:')&&!item.startsWith('Using the existing Capital Plan minimum'));
    const canonicalScenario=scenario;
    if(automatic){
      const facilities=await this.financing.listFacilities(null);
      for(const row of projectRows)facilities.push(...await this.financing.listFacilities(row.id));
      const illustration=illustrateAutomaticPermissions(scenario,facilities,gcSourceSnapshots);
      scenario=illustration.scenario;automaticConditions.push(...illustration.conditions);
    }
    const plans:ProjectPlan[]=[];
    const facilities=await this.financing.listFacilities(null);
    const rates=await this.financing.listRateChangesForProject(null);
    const projectInputs:ProjectPlanInputs[]=[];
    const projectContingencies:Array<{projectId:string;amountCents:number}>=[];
    const sourceInputIssues:string[]=[];
    const scenarioAssumptions:string[]=[];
    for (const row of projectRows) {
      const sourceInputs=await this.projectInputs(row.id,endDate,{synthesizeDraws:false,asOfDate:scenario.asOfDate});
      const review=scenario.sourcePolicies?.projectReviews?.find(item=>item.projectId===row.id);
      const {inputs,issues}=applyFundingProjectReview(sourceInputs,review);sourceInputIssues.push(...issues);
      const stressed=applyFundingRateAssumptions(inputs.facilities,(scenario.sourcePolicies?.loanRateOverrides??[]).filter(item=>inputs.facilities.some(facility=>facility.id===item.facilityId)),scenario.asOfDate);
      inputs.facilities=stressed.facilities;sourceInputIssues.push(...stressed.issues);scenarioAssumptions.push(...stressed.assumptions);
      projectInputs.push(inputs);
      const project=await this.projects?.getProject(row.id);
      const approval=await this.fundingEvidence?.approval?.preview(row.id);
      const contingency=approval?.latest?approval.contingencyRemainingCents:project?.contingencyCents??null;
      if(contingency!==null&&contingency!==undefined)projectContingencies.push({projectId:row.id,amountCents:contingency});
      facilities.push(...await this.financing.listFacilities(row.id));
      rates.push(...await this.financing.listRateChangesForProject(row.id));
    }
    const capitalScenarios=await this.capital.listScenarios();
    if(scenario.acquisitions.some(target=>target.scenarioProjectId&&(scenario.sourcePolicies?.includedScenarioIds??[]).includes(target.scenarioProjectId)))throw new Error("A property cannot be included both as a full scenario and as a separate acquisition target; that would count its purchase funding twice.");
    for(const id of scenario.sourcePolicies?.includedScenarioIds??[]){
      const source=capitalScenarios.find(item=>item.id===id);
      if(!source)throw new Error("A selected scenario is missing from this workspace.");
      if(source.sourceProjectId&&projectRows.some(project=>project.id===source.sourceProjectId))throw new Error("A scenario copied from an included project cannot be counted a second time.");
      projectInputs.push({...this.scenarioInputs(source,endDate),today:scenario.asOfDate});
    }
    // Extend through real source closings/maturities; do not hide payoffs after the owner-target period.
    const relevantMaturities=facilities.filter(f=>f.loanStructure!=="amortizing"||!f.maturityDate||f.maturityDate<=addDays(scenario.asOfDate,3660)).map(f=>f.maturityDate);
    if(facilities.some(f=>f.loanStructure==="amortizing"&&f.maturityDate&&f.maturityDate>addDays(scenario.asOfDate,3660)))scenarioAssumptions.push("Long-term amortizing debt remains outstanding at the displayed forecast horizon; later payments are not presented as paid or modeled through final maturity.");
    endDate=[endDate,...projectInputs.flatMap(input=>[input.expectedExitDate,input.sale?.saleDate,input.scheduleFinish]),...relevantMaturities].filter((date):date is string=>!!date).sort().pop()!;
    if(endDate>addDays(scenario.asOfDate,3660))throw new Error("A source obligation is beyond the supported ten-year forecast; review the horizon explicitly.");
    const rateOverrides=scenario.sourcePolicies?.loanRateOverrides??[];
    const knownFacilityIds=new Set([...facilities.map(f=>f.id),...projectInputs.flatMap(input=>input.facilities.map(f=>f.id))]);
    if(rateOverrides.some(item=>!knownFacilityIds.has(item.facilityId)))throw new Error("A rate assumption references a loan outside this planning workspace.");
    plans.push(await this.businessPlan(endDate,rateOverrides.filter(item=>facilities.some(f=>f.projectId===null&&f.id===item.facilityId)),scenario.asOfDate));
    const expectedFunding:import("../engine/restrictedFunding").ExpectedRestrictedFunding[]=[],eligibilitySources:import("../engine/restrictedFunding").ReimbursementEligibilitySource[]=[],facilityCapacities:import("../engine/restrictedFunding").RestrictedFacilityCapacity[]=[],constructionRecommendations:import("../engine/fundingProjectForecast").ProjectFundingForecast["recommendations"]=[];
    for(const input of projectInputs){
      input.horizonEnd=endDate;
      const recorded=input.subject.kind==="project"&&this.fundingEvidence?await this.fundingEvidence.draws.candidates(input.subject.id):[];
      const projectFacilities=facilities.filter(f=>f.projectId===input.subject.id);
      const constructionRelevant=projectFacilities.some(facility=>facility.rehabFacilityCents!==0);
      const prepared=automatic&&(!input.scheduleStart||!input.scheduleFinish)?automaticUnscheduledProject(input):constructionRelevant?prepareProjectFundingForecast({inputs:input,facilities:projectFacilities,permissions:scenario.facilityPermissions,
        recordedEligibleCosts:recorded.map(cost=>({id:`${cost.source}:${cost.id}`,date:cost.date,amountCents:cost.availableCents,confidence:"recorded" as const})),hardBorrowingCutoff:scenario.preferences.hardBorrowingCutoff})
        :{plan:buildProjectPlan(input),recommendations:[],expectedFunding:[],eligibilitySources:[],facilityCapacities:[],issues:[]};
      plans.push(prepared.plan);expectedFunding.push(...prepared.expectedFunding);eligibilitySources.push(...prepared.eligibilitySources);facilityCapacities.push(...prepared.facilityCapacities);constructionRecommendations.push(...prepared.recommendations);
      sourceInputIssues.push(...prepared.issues);
      if("assumptions" in prepared&&Array.isArray(prepared.assumptions))scenarioAssumptions.push(...prepared.assumptions);
    }
    let evaluatedScenario=scenario;
    let cashProvenance:ReturnType<typeof resolveFundingCashAnchors>["provenance"]=[];
    if(scenario.sourcePolicies?.statementReferences?.length){
      if(!this.fundingEvidence)sourceInputIssues.push("Retained statement evidence is unavailable in this workspace.");
      else{
        const [statements,accounts]=await Promise.all([this.fundingEvidence.bank.listStatements(),this.fundingEvidence.accounting.accounts()]);
        const evidence:StatementCashEvidence[]=[];
        for(const reference of scenario.sourcePolicies.statementReferences){
          const statement=statements.find(item=>item.id===reference.statementId),account=accounts.find(item=>item.id===statement?.accountId);
          if(!statement||!account)continue;
          const forwardStatements=statements.filter(item=>item.accountId===account.id&&item.periodEnd>statement.periodEnd&&item.periodStart<=scenario.asOfDate);
          const rows=(await Promise.all(forwardStatements.map(async item=>(await this.fundingEvidence!.bank.get(item.id)).rows.map(row=>({...row,accountId:account.id,convention:item.convention}))))).flat().filter(row=>row.date>statement.periodEnd&&row.date<=scenario.asOfDate);
          evidence.push({statement,account,rollForward:{fromDate:addDays(statement.periodEnd,1),throughDate:scenario.asOfDate,coverageConfirmed:reference.rollForwardConfirmedThrough===scenario.asOfDate,rows}});
        }
        const resolved=resolveFundingCashAnchors(scenario.cashAnchors,scenario.sourcePolicies.statementReferences,evidence,scenario.asOfDate);
        evaluatedScenario={...scenario,cashAnchors:resolved.anchors};sourceInputIssues.push(...resolved.issues);cashProvenance=resolved.provenance;
      }
    }
    const stressedSources=applyFundingRateAssumptions(facilities.map(facility=>({...facility,rateChanges:rates.filter(rate=>rate.facilityId===facility.id)})),rateOverrides.filter(item=>facilities.some(f=>f.id===item.facilityId)),scenario.asOfDate);
    const sources=stressedSources.facilities;sourceInputIssues.push(...stressedSources.issues);scenarioAssumptions.push(...stressedSources.assumptions);
    const businessAllocations=await this.financing.listFacilityAllocations();
    const conditionalGc=automatic?evaluateConditionalGcCompensationSources(gcSourceSnapshots,{asOfDate:scenario.asOfDate,endDate,facilityPermissions:scenario.facilityPermissions}):null;
    const gcCompensation=conditionalGc?.result??evaluateGcCompensationSources(gcSourceSnapshots,{asOfDate:scenario.asOfDate,endDate,facilityPermissions:scenario.facilityPermissions});
    automaticConditions.push(...conditionalGc?.conditions??[]);
    if(automatic){const closings=illustrateAutomaticClosingReleases(evaluatedScenario,plans);evaluatedScenario=closings.scenario;automaticConditions.push(...closings.conditions);}
    const projection=companyFundingProjection(evaluatedScenario,plans,sources,endDate,variant,{expectedFunding,eligibilitySources,facilityCapacities,businessAllocations,projectContingencies,gcCompensation,issues:sourceInputIssues});
    const {snapshot,sourceIssues}=projection;
    const sourceIdentities=plans.flatMap(plan=>plan.events.map(event=>({id:event.id,source:event.source,date:event.date,amountCents:event.amountCents,actual:event.actual,projectId:plan.subject.id})));
    const qualifications=[...new Set([...projection.qualifications,...scenarioAssumptions,...plans.flatMap(plan=>plan.assumptions.filter(assumption=>!(plan.subject.kind==="business"&&assumption==="Closing date not recorded — planning uses the schedule start.")).map(assumption=>`${plan.subject.name}: ${assumption}`))])];
    const sourceLoanEvidence={facilities:[...facilities].sort((a,b)=>a.id.localeCompare(b.id)),rateChanges:[...rates].sort((a,b)=>a.facilityId.localeCompare(b.facilityId)||a.date.localeCompare(b.date)||a.id.localeCompare(b.id)),
      evaluatedLoanInputs:plans.map(plan=>({subject:plan.subject,loanInputs:[...(plan.loanInputs?.entries()??[])].sort(([a],[b])=>a.localeCompare(b))}))};
    const source={snapshot,cashProvenance,sourceIdentities,sourceLoanEvidence,gcSourceSnapshots,qualifications,variant,engineVersion:FUNDING_ENGINE_VERSION};
    const provenance={engineVersion:FUNDING_ENGINE_VERSION,fingerprint:await fundingFingerprint(source),sourceIdentities,sourceLoanEvidence,gcSourceSnapshots};
    const conditionsByOpportunity=Object.fromEntries(Object.entries(conditionalGc?.conditionsByBindingId??{}).map(([id,conditions])=>[`gc:${id}`,conditions]));
    // Missing confirmations may qualify an illustration. Contradictory/unsupported
    // numerical sources remain blockers and must not be certified by a review flag.
    const reviewOnly=(issue:string)=>issue==='Company cash coverage is not established.'||issue==='Cash is not a confirmed spendable balance.'||issue==='Provide the balance evidence reference.'||issue==='An older balance must be rolled forward with canonical cash records before use.'||issue==='Review coverage of company obligations, taxes, accounts and existing debt service.'||issue.endsWith(': confirm completeness and timing of remaining project costs.')||issue.includes('Bank transactions must explicitly cover every day')||issue.includes('Confirm this statement-backed anchor')||issue.includes('select the retained statement source');
    const optimization=automatic?evaluateAutomaticFunding(snapshot,[...automaticConditions,...sourceIssues.filter(reviewOnly)],sourceIssues.filter(issue=>!reviewOnly(issue)),undefined,conditionsByOpportunity):optimizeCompanyFunding(snapshot);
    if(optimization.status==="feasible"&&(projection.restricted.status==="conditional"||qualifications.length)){
      optimization.status="conditional";for(const strategy of optimization.strategies)if(strategy.status==="feasible")strategy.status="conditional";
      if(automatic){
        const conditions=qualifications.length?qualifications:['Restricted project cash and release qualifications require confirmation.'];
        optimization.evidenceStatus='conditional';optimization.assumptions=[...new Set([...(optimization.assumptions??[]),...conditions])];
        for(const strategy of optimization.strategies)for(const recommendation of strategy.recommendations){recommendation.eligibility='conditional';recommendation.conditions=[...new Set([...(recommendation.conditions??[]),...conditions])];}
      }
    }
    const costOutlook=companyFundingOutlook(plans,snapshot,optimization,sources);
    return {...projection,qualifications,optimization,sourceIssues,constructionRecommendations,cashProvenance,sourceDates:{asOfDate:scenario.asOfDate,endDate},provenance,costOutlook,gcCompensation,canonicalScenario};
  }
  async saveFundingComparison(scenario:CompanyFundingScenario,variant:"expected"|"conservative",revision:number){
    const result=await this.fundingOptimization(scenario,variant);
    return this.capital.recordFundingRun(scenario,revision,{fingerprint:result.provenance.fingerprint,engineVersion:result.provenance.engineVersion,variant,source:{snapshot:result.snapshot,sourceIdentities:result.provenance.sourceIdentities,sourceLoanEvidence:result.provenance.sourceLoanEvidence,gcSourceSnapshots:result.provenance.gcSourceSnapshots,cashProvenance:result.cashProvenance,sourceDates:result.sourceDates,qualifications:result.qualifications},result});
  }
  constructor(
    private readonly db: DbExecutor,
    private readonly proposedCosts: ProposedCostRepository,
    private readonly acquisition: AcquisitionRepository,
    private readonly tasks: TaskRepository,
    private readonly financing: FinancingRepository,
    private readonly capital: CapitalRepository,
    private readonly reconciliation: ReconciliationService,
    private readonly projectFinancials: ProjectFinancialsService,
    private readonly bids: BidService,
    private readonly designs: DesignRepository,
    private readonly projects: ProjectRepository | null = null,
    /** Spec §74S: authorized category budgets and category payment plans. */
    private readonly categoryBudget: CategoryBudgetService | null = null,
  ) {}

  /**
   * PROMOTE A SCENARIO (spec §73P): runs the ORDINARY project creation —
   * real job number, real statuses — never a conversion. The scenario's
   * figures seed the new project (purchase price, expected ARV, strategy,
   * rent, planning start); the scenario itself is archived, its record
   * kept. Scenario data never pollutes accounting.
   */
  async promoteScenario(scenarioId: string): Promise<{ projectId: string; jobNumber: string }> {
    if (!this.projects) throw new Error("Promotion is not available here.");
    const scenario = await this.capital.getScenario(scenarioId);
    if (!scenario) throw new Error("That scenario no longer exists.");
    const project = await this.projects.createProject({
      name: scenario.name,
      statusId: "planning",
      streetAddress: "",
      city: "",
      state: "",
      zip: "",
      purchasePriceCents: scenario.purchasePriceCents,
      expectedArvCents: scenario.expectedSaleCents,
      notes: scenario.notes,
      projectType: "renovation",
    } as never);
    await this.setStrategy(project.id, scenario.strategy);
    if (scenario.strategy === "rental" && scenario.monthlyRentCents !== null) {
      await this.financing.saveRental(project.id, {
        monthlyRentCents: scenario.monthlyRentCents,
        vacancyPctX100: null, propertyTaxAnnualCents: null, insuranceAnnualCents: null,
        hoaMonthlyCents: null, managementPctX100: null, maintenancePctX100: null,
        utilitiesMonthlyCents: null, otherMonthlyCents: null, leaseUpDays: null,
      });
    }
    if (scenario.plannedStartDate) {
      await this.capital.setOverride("project", project.id, scenario.plannedStartDate);
    }
    await this.capital.archiveScenario(scenarioId);
    return { projectId: project.id, jobNumber: project.jobNumber };
  }

  /* ---------------- strategy ---------------- */

  async strategyOf(projectId: string): Promise<Strategy> {
    const rows = await this.db.select<{ strategy: string }>("SELECT strategy FROM projects WHERE id = ?", [
      projectId,
    ]);
    return rows[0]?.strategy === "rental" ? "rental" : "flip";
  }

  /** Switching strategy loses nothing: rental assumptions and facilities stay. */
  async setStrategy(projectId: string, strategy: Strategy): Promise<void> {
    await this.db.execute("UPDATE projects SET strategy = ?, updated_at = ? WHERE id = ?", [
      strategy, new Date().toISOString(), projectId,
    ]);
  }

  /**
   * Spec §75R: injected after construction (like the financing projector
   * and the category basis delta) so the dependency stays one-way — the
   * forecast service consumes this service, never the reverse.
   */
  setForecastBridge(fn: (projectId: string) => Promise<ForecastBridge>): void {
    this.forecastBridge = fn;
  }

  private forecastBridge: ((projectId: string) => Promise<ForecastBridge>) | null = null;

  /* ---------------- one project's inputs ---------------- */

  async projectInputs(
    projectId: string,
    horizonEnd: string,
    options: { synthesizeDraws?: boolean; asOfDate?:string } = {},
  ): Promise<ProjectPlanInputs> {
    const asOfDate=options.asOfDate??today();
    const [projectRows, financingFacilities, draws, paymentPlans, rental, sale, taskSnapshot, rateChanges, events] =
      await Promise.all([
        this.db.select<{
          id: string; name: string; strategy: string; purchase_price_cents: number | null;
          expected_arv_cents: number | null; purchase_date: string | null;
        }>(
          "SELECT id, name, strategy, purchase_price_cents, expected_arv_cents, purchase_date FROM projects WHERE id = ?",
          [projectId],
        ),
        this.financing.listFacilities(projectId),
        this.financing.listDraws(projectId),
        this.financing.listPaymentPlans(projectId),
        this.financing.getRental(projectId),
        this.projectFinancials.getSale(projectId),
        this.tasks.scheduleWithTasks(projectId),
        this.financing.listRateChangesForProject(projectId),
        this.reconciliation.eventsForProject(projectId),
      ]);
    const { schedule, tasks: taskList } = taskSnapshot;
    const project = projectRows[0];
    if (!project) throw new Error("That project no longer exists.");
    const acquisitionLines = await this.acquisition.listByProject(projectId);

    /* Working estimate per feature for the ACTIVE design + common scope,
     * with selected quotes substituting their covered features exactly as
     * the estimate screen does (spec §69-6 — same pure engine). */
    const activeDesignId = await this.designs.getActiveDesignId(projectId);
    const lines = await this.proposedCosts.listForDesign(projectId, activeDesignId);
    const active = lines.filter((l) => l.archivedAt === null);
    const estimateByFeature = new Map<string, Cents>();
    const featureNames = new Map<string, string>();
    for (const line of active) {
      const key = line.featureId ?? "";
      estimateByFeature.set(key, (estimateByFeature.get(key) ?? 0) + line.amountCents);
    }
    // Feature display names for event labels.
    const featureRows = await this.db.select<{ id: string; name: string; work_status:string|null }>(
      "SELECT id, name, work_status FROM project_features WHERE project_id = ?",
      [projectId],
    );
    for (const row of featureRows) featureNames.set(row.id, row.name);
    const completedFeatures=new Set(featureRows.filter(row=>row.work_status==="complete").map(row=>row.id));
    for(const featureId of completedFeatures)estimateByFeature.delete(featureId);
    featureNames.set("", "Unassigned scope");

    // A feature claimed by a quote is remapped onto the quote's own key so
    // its actuals still net against the quoted figure (never double count).
    const featureRemap = new Map<string, string>();
    try {
      const quotes = await this.bids.listQuotes(projectId);
      const selected = quotes
        .filter((q) => q.selectedAt !== null)
        .map((q) => ({ quoteId: q.id, contractor: q.contractor, amountCents: q.amountCents, coverage: q.coverage }));
      if (selected.length > 0) {
        const model = await this.bids.model(projectId, activeDesignId);
        const substituted = applySelectedQuotes(
          active.map((l) => ({ featureId: l.featureId, amountCents: l.amountCents, archived: false })),
          model,
          selected,
        );
        for (const sub of substituted.substitutions) {
          const key = `quote:${sub.quoteId}`;
          for (const featureId of sub.featureIds) {
            estimateByFeature.delete(featureId);
            featureRemap.set(featureId, key);
          }
          // A finished scope's under-budget variance is history, not an
          // invoice still to come. Keep actual attribution on the quote key.
          if(!sub.featureIds.length || !sub.featureIds.every(id=>completedFeatures.has(id)))estimateByFeature.set(key, sub.quotedCents);
          featureNames.set(key, `${sub.contractor} — selected quote`);
        }
      }
    } catch {
      /* no bids yet: the calculated estimate stands */
    }

    // The CPM's dates, joined with each task's feature link.
    const featureOfTask = new Map(taskList.map((t) => [t.id, t.featureId]));
    const scheduledTasks = schedule.tasks.map((t) => ({
      id: t.id,
      name: t.name,
      start: t.start,
      finish: t.finish,
      featureId: featureOfTask.get(t.id) ?? null,
    }));

    /* CATEGORY-LEVEL PLANNING TRUTHS (spec §74S/V). Authorized category
     * budgets adjust the projection by their signed delta over the
     * quote-adjusted calculation; a category PAYMENT PLAN collapses that
     * category into one stream so its tranches govern the timing. Both
     * paths are rare and probe cheaply before deriving the model. */
    const categoryAdjustments: NonNullable<ProjectPlanInputs["categoryAdjustments"]> = [];
    const remainingByFeature=new Map<string,Cents>();
    const mixedSourceIds=new Set<string>();
    const categoryPlans = paymentPlans.filter((p) => p.sourceKind === "category" && p.sourceId);
    if (this.categoryBudget) {
      try {
        const authorized = await this.categoryBudget.listAuthorized(projectId);
        const hasAuthorized = authorized.some((a) => a.authorizedCents !== null);
        const hasMixed=(await this.db.select("SELECT id FROM labor_settlements WHERE project_id=? AND coverage_kind='mixed-work' LIMIT 1",[projectId])).length>0;
        if (hasAuthorized || categoryPlans.length > 0 || hasMixed) {
          const categoryModel = await this.categoryBudget.model(projectId);
          const latestFinishFor = (featureIds: readonly string[]): string => {
            let latest: string | null = null;
            for (const task of scheduledTasks) {
              if (task.featureId && featureIds.includes(task.featureId)) {
                if (latest === null || task.finish > latest) latest = task.finish;
              }
            }
            return latest ?? schedule.projectFinish ?? horizonEnd;
          };
          const planned = new Set(categoryPlans.map((p) => p.sourceId as string));
          for (const row of categoryModel.rows) {
            if(row.settledFeatureIds?.length) {
              // The category engine owns the unsplit group's remaining work.
              // Keep it aggregated; no source cash or per-item Actual is synthesized.
              const key=`category:${row.categoryId}`;
              for(const id of row.featureIds){estimateByFeature.delete(id);featureRemap.set(id,key);}
              for(const quote of row.quotes)estimateByFeature.delete(`quote:${quote.quoteId}`);
              if(row.categoryId.split("+").includes("other"))estimateByFeature.delete("");
              estimateByFeature.set(key,row.currentBasisCents);
              featureNames.set(key,row.displayName);
              remainingByFeature.set(key,row.remainingCents);
              for(const id of row.mixedSettlementSourceIds??[])mixedSourceIds.add(id);
              continue;
            }
            if (planned.has(row.categoryId)) {
              // One stream for the whole category: its current basis under
              // a category key; features and quote keys leave the map so
              // nothing counts twice; actuals net against the same key.
              const key = `category:${row.categoryId}`;
              for (const featureId of row.featureIds) {
                estimateByFeature.delete(featureId);
                featureRemap.set(featureId, key);
              }
              for (const quote of row.quotes) estimateByFeature.delete(`quote:${quote.quoteId}`);
              if (!row.complete) estimateByFeature.set(key, row.currentBasisCents);
              featureNames.set(key, row.displayName);
            } else if (row.authorizedCents !== null && !row.complete) {
              const delta = row.authorizedCents - row.quoteAdjustedCents;
              if (delta !== 0) {
                categoryAdjustments.push({
                  categoryId: row.categoryId,
                  label: `${row.displayName} — authorized budget adjustment`,
                  deltaCents: delta,
                  date: latestFinishFor(row.featureIds),
                });
              }
            }
          }
        }
      } catch {
        /* the calculated + quoted basis stands */
      }
    }

    // Dated actual outflows: the SAME normalized event stream Phase 12
    // consumes — expenses + purchase allocations + mileage, tax included,
    // counted once. Acquisition-bucket money is excluded here because the
    // acquisition lines carry it (never duplicated, Phase 2 rule). Runs
    // AFTER every remap so a claimed feature's actuals net against the
    // quote or category key that now carries its projection.
    const [categoryBuckets, nonCashRows] = await Promise.all([
      this.db.select<{id:string; default_bucket:FinancialBucket}>("SELECT id, default_bucket FROM expense_categories"),
      this.db.select<{id:string}>(`SELECT a.id FROM purchase_allocations a JOIN purchase_lines l ON l.id=a.line_id JOIN purchases p ON p.id=l.purchase_id WHERE a.project_id=? AND (p.refund_method='store-credit' OR (p.kind='return' AND (p.refund_method IS NULL OR p.refund_method NOT IN ('cash','original-payment'))) OR lower(replace(p.payment_method,' ','')) IN ('store-credit','storecredit'))`,[projectId]),
    ]);
    const buckets=new Map(categoryBuckets.map(row=>[row.id,row.default_bucket]));
    const nonCashIds=new Set(nonCashRows.map(row=>row.id));
    const live = events.filter((e) => e.voidedAt === null && (e.bucketOverride ?? buckets.get(e.categoryId)) !== "acquisition");
    const lineFeature = new Map(active.map((l) => [l.id, l.featureId]));
    const actualCosts = live.map((e) => {
      const classification=classifyActualCash(e,buckets.get(e.categoryId) ?? "renovation",nonCashIds);
      const featureId =
        e.linkedFeatureId ??
        (e.linkedProposedLineId ? (lineFeature.get(e.linkedProposedLineId) ?? null) : null);
      return {
        date: e.date,
        amountCents: e.amountCents + e.taxCents,
        label: e.description || "Recorded cost",
        featureId: featureId ? (featureRemap.get(featureId) ?? featureId) : null,
        sourceKind: e.source,
        sourceId: e.id,
        cashAmountCents: classification.cashCents,
        bucket: classification.bucket,
      };
    });

    const ratesByFacility = new Map<string, typeof rateChanges>();
    for (const rate of rateChanges) {
      const rates = ratesByFacility.get(rate.facilityId) ?? [];
      rates.push(rate);
      ratesByFacility.set(rate.facilityId, rates);
    }
    const facilities: FacilityInput[] = financingFacilities.map(f => ({ ...f, rateChanges: ratesByFacility.get(f.id) ?? [] }));
    const activeExpenseIds=new Set(live.filter(e=>e.source==="expense").map(e=>e.id));
    const recordedLoanActivities=await this.financing.listLoanActivities(projectId);
    const loanActivityById=new Map(recordedLoanActivities.map(a=>[a.id,a]));
    const loanActivities=recordedLoanActivities.map(activity=>{
      // Voiding its cost source does not erase the principal half of a
      // payment. Keep both original/reversal principal events together.
      const costSource=activity.expenseId ?? (activity.reversalOfId?loanActivityById.get(activity.reversalOfId)?.expenseId:null);
      return costSource && !activeExpenseIds.has(costSource) ? {...activity,interestCents:0,feeCents:0} : activity;
    });
    const additionalEvents:FinancialEvent[]=[];
    const commitmentCoverage=new Map<string,number>();
    const commitmentActualCoverage=new Map<string,number>();
    if(this.commitmentSource){
      const commitments=await this.commitmentSource.model(projectId,addDays(asOfDate,-365),horizonEnd,asOfDate);
      for(const occurrence of commitments.occurrences){
        if(occurrence.linkedFeatureId && occurrence.status!=="canceled"){
          const key=featureRemap.get(occurrence.linkedFeatureId)??occurrence.linkedFeatureId;
          commitmentCoverage.set(key,(commitmentCoverage.get(key)??0)+Math.max(0,occurrence.expectedCents-occurrence.actualCents));
          for(const match of occurrence.matches){const actual=actualCosts.find(e=>e.sourceKind===match.actualSource && e.sourceId===match.actualId);if(actual && !actual.featureId && actual.bucket==="renovation"&&!mixedSourceIds.has(match.actualId))commitmentActualCoverage.set(key,(commitmentActualCoverage.get(key)??0)+match.amountCents);}
        }
        if(occurrence.remainingCents>0)additionalEvents.push({id:`commitment:${occurrence.key}`,date:occurrence.dueDate,amountCents:-occurrence.remainingCents,kind:"commitment",label:occurrence.name,subject:{kind:"project",id:project.id,name:project.name},facilityId:null,source:{kind:"commitment",id:occurrence.commitmentId},assumption:false,actual:false,confidence:"planning",status:occurrence.status,costBucket:buckets.get(occurrence.categoryId)});
      }
      for(const activity of commitments.activities){const effect=cashActivityEffect(activity);additionalEvents.push({id:`cash-activity:${activity.id}`,date:activity.date,amountCents:effect.cashCents,kind:activity.kind,label:activity.name,subject:{kind:"project",id:project.id,name:project.name},facilityId:null,source:{kind:"cash-activity",id:activity.id},assumption:false,actual:true,confidence:"high"});}
    }

    /* Spec §75R: the multi-project plan sees the SAME planned draws the
     * single-project execution forecast derives. One engine decides when
     * the lender sends money; this path merely consumes its answer. The
     * forecast itself passes synthesizeDraws:false and adds them itself. */
    let plannedDraws: DrawInput[] = draws;
    let featureWorkFinish: Map<string, string> | undefined;
    let expectedExitDate: string | null | undefined;
    if (options.synthesizeDraws !== false && this.forecastBridge) {
      try {
        const bridge = await this.forecastBridge(projectId);
        plannedDraws = [...draws, ...bridge.draws];
        featureWorkFinish = bridge.featureWorkFinish;
        expectedExitDate = bridge.expectedExitDate;
      } catch {
        /* the recorded draws and the plain schedule stand on their own */
      }
    }

    return {
      subject: { kind: "project", id: project.id, name: project.name },
      strategy: project.strategy === "rental" ? "rental" : "flip",
      purchasePriceCents: project.purchase_price_cents === null ? null : Number(project.purchase_price_cents),
      acquisitionCostsCents: acquisitionExpensesCents(acquisitionLines),
      expectedArvCents: project.expected_arv_cents === null ? null : Number(project.expected_arv_cents),
      purchaseDate: project.purchase_date,
      scheduleStart: schedule.startDate,
      scheduleFinish: schedule.projectFinish,
      tasks: scheduledTasks,
      estimateByFeature,
      remainingByFeature,
      featureNames,
      actualCosts,
      facilities,
      draws: plannedDraws,
      featureWorkFinish,
      expectedExitDate,
      paymentPlans,
      loanActivities,
      additionalEvents,
      commitmentCoverage,
      commitmentActualCoverage,
      categoryAdjustments,
      rental,
      sale: sale
        ? {
            saleDate: sale.saleDate,
            // When selling costs are recorded in accounting they already
            // appear as actual events; net here would double-count them.
            netProceedsCents: sale.costsRecordedInAccounting
              ? sale.grossSalePriceCents
              : sale.grossSalePriceCents -
                sale.commissionCents - sale.sellerClosingCents - sale.concessionsCents - sale.otherSellingCents,
            closedAt: sale.closedAt,
          }
        : null,
      today: asOfDate,
      horizonEnd,
    };
  }

  async projectPlan(projectId: string, horizonEnd?: string): Promise<ProjectPlan> {
    const end = horizonEnd ?? (await this.horizonEnd());
    return buildProjectPlan(await this.projectInputs(projectId, end));
  }

  /** Calendar is a bounded projection of the same financial source events. */
  async calendar(projectId: string | null, from: string, to: string, asOf = today()): Promise<FinancialCalendarModel> {
    // Validate before any expensive reads.
    financialCalendar([], from, to, asOf);
      const summaryThrough=addDays(asOf,30);
      const horizon=to>summaryThrough?to:summaryThrough;
    if (projectId) {
        const plan=await this.projectPlan(projectId,horizon);
      return financialCalendar(plan.events,from,to,asOf,plan.assumptions);
    }
      const portfolio=await this.portfolio("expected",horizon);
    return financialCalendar([...portfolio.plans.flatMap(p=>p.events),...(portfolio.businessEvents??[])],from,to,asOf,portfolio.result.assumptions);
  }

  async businessCalendar(from:string,to:string,asOf=today()):Promise<FinancialCalendarModel>{
    financialCalendar([],from,to,asOf);
    const through=addDays(asOf,30),plan=await this.businessPlan(to>through?to:through);
    return financialCalendar(plan.events,from,to,asOf,plan.assumptions);
  }

  async businessPlan(horizonEnd?:string,rateOverrides:readonly FundingLoanRateOverride[]=[],asOfDate=today()):Promise<ProjectPlan> {
    horizonEnd??=await this.horizonEnd();
    const facilities=await this.financing.listFacilities(null);
    const rateChanges=await this.financing.listRateChangesForProject(null);
    const ratesByFacility=new Map<string,typeof rateChanges>();
    for(const rate of rateChanges){const rates=ratesByFacility.get(rate.facilityId)??[];rates.push(rate);ratesByFacility.set(rate.facilityId,rates);}
    const draws=await this.financing.listDraws(null);
    const loanActivities=await this.financing.listLoanActivities(null);
    const subject={kind:"business" as const,id:"business",name:"Business commitments"};
    const additionalEvents:FinancialEvent[]=[];
    const actualCosts:ProjectPlanInputs["actualCosts"]=[];
    if(this.commitmentSource){
      const source=await this.commitmentSource.businessModel(addDays(asOfDate,-365),horizonEnd,asOfDate);
      for(const occurrence of source.occurrences.filter(o=>o.projectId===null && o.remainingCents>0))additionalEvents.push({id:`commitment:${occurrence.key}`,date:occurrence.dueDate,amountCents:-occurrence.remainingCents,kind:"commitment",label:occurrence.name,subject:{...subject,kind:"business"},facilityId:null,source:{kind:"commitment",id:occurrence.commitmentId},assumption:false,actual:false,confidence:"planning",status:occurrence.status});
      for(const activity of source.activities.filter(a=>a.projectId===null))additionalEvents.push({id:`cash-activity:${activity.id}`,date:activity.date,amountCents:cashActivityEffect(activity).cashCents,kind:activity.kind,label:activity.name,subject:{...subject,kind:"business"},facilityId:null,source:{kind:"cash-activity",id:activity.id},assumption:false,actual:true,confidence:"high"});
      for(const payment of source.companyPayments??[]){
        if(payment.voidedAt!==null)continue;
        const effect=companyPaymentEffect(payment);
        actualCosts.push({date:payment.date,amountCents:effect.costCents,cashAmountCents:effect.cashCents,label:payment.description,featureId:null,sourceKind:"company-purchase",sourceId:payment.id,bucket:"other"});
      }
    }
    const stressed=applyFundingRateAssumptions(facilities.map(f=>({...f,rateChanges:ratesByFacility.get(f.id)??[]})),rateOverrides,asOfDate);
    if(stressed.issues.length)throw new Error(stressed.issues.join(" "));
    const inputs:ProjectPlanInputs={subject,strategy:"rental",purchasePriceCents:null,acquisitionCostsCents:0,expectedArvCents:null,purchaseDate:null,scheduleStart:null,scheduleFinish:null,tasks:[],estimateByFeature:new Map(),featureNames:new Map(),actualCosts,facilities:stressed.facilities,draws,paymentPlans:[],loanActivities,additionalEvents,rental:null,sale:null,today:asOfDate,horizonEnd};
    const plan=buildProjectPlan(inputs);
    return{...plan,subject:{...plan.subject,kind:"business"},events:plan.events.map(e=>({...e,subject:{...e.subject,kind:"business"}}))};
  }

  async businessFinancingSnapshot():Promise<FacilitySummary[]> {
    return summarizeFacilities(await this.businessPlan(await this.horizonEnd()),today());
  }

  /**
   * Spec §73.1H: the Phase 12 dashboard's financing projector — interest
   * and fees the loan ledger still expects after today. Projects with no
   * financing answer with ONE query and zero.
   */
  async projectedFutureFinancing(projectId: string): Promise<Cents> {
    const facilities = await this.financing.listFacilities(projectId);
    if (facilities.length === 0) return 0;
    const plan = await this.projectPlan(projectId);
    return projectedFutureFinancingCents(plan, today());
  }

  /**
   * Compact single-project financing snapshot for the Overview card
   * (spec §73.1P). Null — cheaply — when the project has no financing.
   */
  async financingSnapshot(projectId: string): Promise<{
    position: CashPosition;
    facilities: FacilitySummary[];
  } | null> {
    const facilities = await this.financing.listFacilities(projectId);
    if (facilities.length === 0) return null;
    const plan = await this.projectPlan(projectId);
    const now = today();
    return { position: cashPosition(plan, now), facilities: summarizeFacilities(plan, now) };
  }

  /**
   * The stabilized-rental headline numbers (spec §73L/M) — NOI vs cash
   * flow, cap rate, DSCR, cash-on-cash, equity. All figures come from the
   * pure engine; debt and its service are read off the plan's own ledgers
   * at the horizon. Null when the project has no rental assumptions.
   */
  async rentalSummary(projectId: string, horizonEnd?: string): Promise<RentalEconomics | null> {
    const end = horizonEnd ?? (await this.horizonEnd());
    const inputs = await this.projectInputs(projectId, end);
    if (!inputs.rental) return null;
    const plan = buildProjectPlan(inputs);
    return stabilizedRentalEconomics(plan, inputs.rental, inputs.expectedArvCents);
  }

  /* ---------------- scenario fixtures ---------------- */

  /**
   * A scenario is a handful of typed figures, so its inputs are synthesized:
   * one renovation span, one optional facility. Everything unknown stays
   * unknown and the engine labels its own fallbacks.
   */
  scenarioInputs(scenario: ScenarioProject, horizonEnd: string): ProjectPlanInputs {
    const start = scenario.plannedStartDate ?? today();
    const finish = scenario.durationDays !== null ? addDays(start, scenario.durationDays) : null;
    const exitDate =
      finish !== null ? addDays(finish, scenario.holdDaysAfterRehab ?? 0) : null;
    const netSale =
      scenario.expectedSaleCents !== null
        ? scenario.expectedSaleCents -
          (scenario.sellingCostsPctX100 !== null
            ? Math.round((scenario.expectedSaleCents * scenario.sellingCostsPctX100) / 10000)
            : 0)
        : null;
    const facilities: FacilityInput[] =
      scenario.financingCommitmentCents !== null
        ? [
            {
              id: `scenario-loan-${scenario.id}`,
              name: "Assumed financing",
              loanType: "acquisition-rehab",
              commitmentCents: scenario.financingCommitmentCents,
              acquisitionFundingCents:
                scenario.purchasePriceCents !== null && scenario.downPaymentCents !== null
                  ? Math.max(0, Math.min(scenario.financingCommitmentCents, scenario.purchasePriceCents - scenario.downPaymentCents))
                  : scenario.financingCommitmentCents,
              rehabFacilityCents: null,
              downPaymentCents: scenario.downPaymentCents,
              interestRatePctX100: scenario.interestRatePctX100,
              interestBasis: "drawn",
              interestOnly: true,
              originationPointsX100: null,
              originationFeeCents: scenario.originationFeeCents,
              otherFeesCents: null,
              drawFeeCents: null,
              paymentCadence: "monthly",
              closingDate: start,
              firstPaymentDate: null,
              maturityDate: null,
              refinanceLtvPctX100: null,
              refinanceClosingCostsCents: null,
              payoffFacilityId: null,
              expectedDate: null,
            },
          ]
        : [];
    return {
      subject: { kind: "scenario", id: scenario.id, name: scenario.name },
      strategy: scenario.strategy,
      purchasePriceCents: scenario.purchasePriceCents,
      acquisitionCostsCents: 0,
      expectedArvCents: netSale,
      purchaseDate: null,
      scheduleStart: start,
      scheduleFinish: exitDate ?? finish,
      tasks:
        finish !== null
          ? [{ id: `scenario-rehab-${scenario.id}`, name: "Renovation", start, finish, featureId: "scenario-rehab" }]
          : [],
      estimateByFeature: new Map(scenario.rehabCents !== null ? [["scenario-rehab", scenario.rehabCents]] : []),
      featureNames: new Map([["scenario-rehab", "Renovation (scenario)"]]),
      actualCosts: [],
      facilities,
      draws: [],
      paymentPlans: [],
      rental:
        scenario.strategy === "rental" && scenario.monthlyRentCents !== null
          ? {
              monthlyRentCents: scenario.monthlyRentCents,
              vacancyPctX100: null,
              propertyTaxAnnualCents: null,
              insuranceAnnualCents: null,
              hoaMonthlyCents: null,
              managementPctX100: null,
              maintenancePctX100: null,
              utilitiesMonthlyCents: null,
              otherMonthlyCents: null,
              leaseUpDays: null,
            }
          : null,
      sale: null,
      today: today(),
      horizonEnd,
    };
  }

  /* ---------------- the portfolio ---------------- */

  private async horizonEnd(): Promise<string> {
    const settings = await this.capital.getPlanSettings();
    let end = today();
    for (let i = 0; i < settings.horizonMonths; i++) end = addMonth(end);
    return end;
  }

  /** Plans (overrides applied), capital and settings — one assembly. */
  private async assemble(requestedHorizon?: string): Promise<{
    plans: ProjectPlan[];
    capital: CapitalInputs;
    capitalSourceCount: number;
    settings: PlanSettings;
    horizonEnd: string;
  }> {
    const settings = await this.capital.getPlanSettings();
    const horizonEnd = requestedHorizon ?? await this.horizonEnd();
    const [projectRows, scenarios, overrides, capitalSources] = await Promise.all([
      this.db.select<{ id: string }>("SELECT id FROM projects ORDER BY created_at"),
      this.capital.listScenarios(),
      this.capital.listOverrides(),
      this.capital.listCapitalSources(),
    ]);

    const overrideFor = new Map(overrides.map((o) => [`${o.subjectKind}:${o.subjectId}`, o.startDate]));

    let plans: ProjectPlan[] = [];
    for (const row of projectRows) {
      plans.push(buildProjectPlan(await this.projectInputs(row.id, horizonEnd)));
    }
    for (const scenario of scenarios) {
      plans.push(buildProjectPlan(this.scenarioInputs(scenario, horizonEnd)));
    }

    plans = plans.map((plan) => {
      const override = overrideFor.get(`${plan.subject.kind}:${plan.subject.id}`);
      return override ? shiftPlan(plan, override) : plan;
    });

    // Capital: cash-like sources sum; the LOCs merge into one bridging
    // facility (limits and balances sum; the highest APR is used so the
    // bridge never understates carrying cost).
    const cash = capitalSources
      .filter((s) => s.kind !== "loc")
      .reduce((sum, s) => sum + (s.amountCents ?? 0), 0);
    const linkedSources=new Set((await this.financing.listFacilities(null)).map(f=>f.capitalSourceId).filter(Boolean));
    const locs = capitalSources.filter((s) => s.kind === "loc" && !linkedSources.has(s.id));
    const capital: CapitalInputs = {
      cashCents: cash,
      loc:
        locs.length > 0
          ? {
              limitCents: locs.reduce((sum, s) => sum + (s.locLimitCents ?? 0), 0),
              balanceCents: locs.reduce((sum, s) => sum + (s.locBalanceCents ?? 0), 0),
              aprPctX100: locs.reduce<number | null>(
                (max, s) => (s.aprPctX100 === null ? max : Math.max(max ?? 0, s.aprPctX100)),
                null,
              ),
            }
          : null,
    };

    return { plans, capital, capitalSourceCount: capitalSources.length, settings, horizonEnd };
  }

  /**
   * Every open project plus every scenario fixture, shifted by planning
   * overrides, merged against the business's capital. `variant`
   * "conservative" applies the user-configured stress transform to every
   * non-actual event (spec §73U).
   */
  async portfolio(variant: PlanVariant = "expected",horizonEnd?:string): Promise<PortfolioPlan> {
    const assembled = await this.assemble(horizonEnd);
    let plans = assembled.plans;
    if (variant === "conservative") {
      plans = plans.map((plan) =>
        applyConservative(plan, {
          overrunPctX100: assembled.settings.conservativeOverrunPctX100,
          durationPctX100: assembled.settings.conservativeDurationPctX100,
          drawDelayDays: assembled.settings.conservativeDrawDelayDays,
          saleDelayDays: assembled.settings.conservativeSaleDelayDays,
        }),
      );
    }
    const business=await this.businessPlan(assembled.horizonEnd);
    const businessEvents=business.events.map(e=>({...e,subject:{...e.subject,kind:"business" as const}}));
    const result = combinePortfolio(plans, assembled.capital, assembled.horizonEnd, assembled.settings.minReserveCents,{...business,events:businessEvents});
    return {
      variant,
      horizonEnd: assembled.horizonEnd,
      plans,
      result,
      settings: assembled.settings,
      capital: assembled.capital,
      capitalSourceCount: assembled.capitalSourceCount,
      businessEvents,
      businessFacilities: summarizeFacilities(business,today()),
    };
  }

  /**
   * Recommend PROJECT-START dates under the chosen objective (spec §73S).
   * Pure, bounded, deterministic — the result is a set of suggested
   * PLANNING overrides the user applies explicitly; nothing is written.
   */
  async optimize(
    objective: PlanObjective,
    maxConcurrent: number | null = null,
  ): Promise<OptimizerResult> {
    const assembled = await this.assemble();
    return optimizeStarts(assembled.plans, assembled.capital, objective, {
      minReserveCents: assembled.settings.minReserveCents,
      horizonEnd: assembled.horizonEnd,
      today: today(),
      maxConcurrent,
    },await this.businessPlan(assembled.horizonEnd));
  }

  /** Labelled, derived statements about the current plan (spec §73V/X/Y). */
  async recommendations(variant: PlanVariant = "expected"): Promise<Recommendation[]> {
    const plan = await this.portfolio(variant);
    return this.recommendationsForPlan(plan);
  }

  /** Derive the visible recommendations from the exact displayed snapshot. */
  recommendationsForPlan(plan: PortfolioPlan): Recommendation[] {
    return buildRecommendations(plan.result, plan.plans, plan.capital, plan.settings.minReserveCents);
  }
}
